import React from 'react';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { Table } from 'apps/sigma2-ui/src/components/ui/Table';
import { Setter } from 'libs/end-to-end/src/ui-types';
import { BaseRubricType } from 'libs/end-to-end/src/types/ai-interviewer/base-rubric';

const createDefaultRubricItem = (weight = 0, description = 'New Criteria') => ({
  weight,
  description,
});

export const createResumeGradingRubric = (scoreKey = 'new_score_key') => ({
  score_key: scoreKey,
  rubric: [createDefaultRubricItem()],
});

export const createQuestionRubric = (question = 'New Question') => ({
  question,
  rubric: [createDefaultRubricItem()],
});

const RubricCriteriaComponent = <T extends BaseRubricType>({
  item,
  rowIndex,
  mapping,
  setMapping,
}: {
  item: T;
  rowIndex: number;
  mapping: T[];
  setMapping: Setter<T[]>;
}) => {
  const addCriteria = (rowIndex: number) => {
    // Add a row to the mapping table
    const newMapping = [...mapping];
    newMapping[rowIndex].rubric.push({
      weight: 0,
      description: '',
    });

    // Do not allow more than 5 criteria, since this is the limit
    if (newMapping[rowIndex].rubric.length > 5) {
      return;
    }

    setMapping(newMapping);
  };

  const removeCriteria = (rowIndex: number, criteriaIndex: number) => {
    const newMapping = [...mapping];
    newMapping[rowIndex].rubric.splice(criteriaIndex, 1);
    setMapping(newMapping);
  };

  return (
    <Spacing size={'md'} direction={'vertical'}>
      <Button
        behavior={{
          type: 'onClick',
          onClick: () => {
            addCriteria(rowIndex);
          },
        }}
        disabled={item.rubric.length >= 5}
        text={'Add criteria'}
      />
      <Table
        items={item.rubric ?? []}
        columnDefinitions={[
          {
            id: 'id',
            header: () => <div>ID</div>,
            cell: ({ rowIndex }) => <div>{rowIndex + 1}</div>,
          },
          {
            id: 'description',
            header: () => <div>Description</div>,
            cell: ({ item, rowIndex: criteriaIndex }) => (
              <textarea
                style={{
                  width: '100%',
                }}
                className="form-textarea"
                key={`description_${rowIndex}_${criteriaIndex}`}
                value={item.description}
                aria-label={`description_${rowIndex}_${criteriaIndex}`}
                onChange={(event) => {
                  // Update the rubric value in the mapping
                  const newMapping = [...mapping];
                  newMapping[rowIndex].rubric[criteriaIndex].description =
                    event.target.value;
                  setMapping(newMapping);
                }}
              />
            ),
          },
          {
            id: 'weight',
            header: () => <div>Weight</div>,
            cell: ({ item, rowIndex: criteriaIndex }) => (
              <input
                className="form-input"
                type="number"
                style={{
                  width: '100%',
                }}
                key={`weight_${rowIndex}_${criteriaIndex}`}
                value={item.weight}
                aria-label={`weight_${rowIndex}_${criteriaIndex}`}
                onChange={(event) => {
                  // Update the rubric value in the mapping
                  const newMapping = [...mapping];
                  newMapping[rowIndex].rubric[criteriaIndex].weight = Number(
                    event.target.value,
                  );
                  setMapping(newMapping);
                }}
              />
            ),
          },
          {
            id: 'action',
            header: () => <div>Actions</div>,
            cell: ({ rowIndex: criteriaIndex }) => (
              <Spacing size={'md'} direction={'vertical'}>
                <Button
                  behavior={{
                    type: 'onClick',
                    onClick: () => {
                      removeCriteria(rowIndex, criteriaIndex);
                    },
                  }}
                  text={'Remove'}
                />
              </Spacing>
            ),
          },
        ]}
      />
    </Spacing>
  );
};

export const DataSetRubricMappingComponent = <T extends BaseRubricType>({
  mapping,
  setMapping,
  titleComponent,
  descriptionComponent,
  onAddItem,
  itemDisplayField,
}: {
  mapping: T[];
  setMapping: Setter<T[]>;
  titleComponent?: React.ReactNode;
  descriptionComponent?: React.ReactNode;
  onAddItem: () => T; // Function to create new item
  itemDisplayField: keyof T; // Field to display in the item column
}) => {
  const addItem = () => {
    const newMapping = [...mapping];
    newMapping.push(onAddItem());
    setMapping(newMapping);
  };

  const removeItem = (rowIndex: number) => {
    const newMapping = [...mapping];
    newMapping.splice(rowIndex, 1);
    setMapping(newMapping);
  };

  return (
    <Spacing size={'md'} direction={'vertical'}>
      {titleComponent}
      {descriptionComponent}
      <Spacing size={'md'} direction={'horizontal'}>
        <Button
          behavior={{
            type: 'onClick',
            onClick: addItem,
          }}
          text={'Add item'}
        />
      </Spacing>
      <Table
        items={mapping ?? []}
        columnDefinitions={[
          {
            id: 'id',
            header: () => <div>ID</div>,
            cell: ({ rowIndex }) => <div>{rowIndex + 1}</div>,
          },
          {
            id: 'item',
            header: () => <div>Item</div>,
            cell: ({ item, rowIndex }) => (
              <textarea
                style={{
                  width: '100%',
                }}
                className="form-textarea"
                key={`item_${rowIndex}`}
                value={item[itemDisplayField] as string}
                aria-label={`item_${rowIndex}`}
                onChange={(event) => {
                  const newMapping = [...mapping];
                  newMapping[rowIndex] = {
                    ...newMapping[rowIndex],
                    [itemDisplayField]: event.target.value,
                  };
                  setMapping(newMapping);
                }}
              />
            ),
          },
          {
            id: 'rubric',
            header: () => <div>Rubric</div>,
            cell: ({ item, rowIndex }) => (
              <RubricCriteriaComponent
                item={item}
                rowIndex={rowIndex}
                mapping={mapping}
                setMapping={setMapping}
              />
            ),
          },
          {
            id: 'action',
            header: () => <div>Actions</div>,
            cell: ({ rowIndex }) => (
              <Spacing size={'md'} direction={'vertical'}>
                <Button
                  behavior={{
                    type: 'onClick',
                    onClick: () => {
                      removeItem(rowIndex);
                    },
                  }}
                  text={'Remove'}
                />
              </Spacing>
            ),
          },
        ]}
      />
    </Spacing>
  );
};
